import { JournalEntryScore } from '../../model/dto/journal-entry/journal-entry-score';
import {
  DirectionalIntensity,
  NotReportedIntensity,
  RawSymptomDefinition,
  SeverityIntensity,
  SymptomIntensity,
  SymptomScore,
} from '../../model/type/symptom-intensity.type';
import { symptomDefinitions } from '../../model/type/symptom.type';
import {
  DirectionalSymptomKey,
  getSymptomDefinition,
  isDirectionalSymptom,
  SeveritySymptomKey,
  SymptomKey,
} from '../../symptom-catalogue';

const NOT_REPORTED: NotReportedIntensity = {
  state: 'not_reported',
  score: null,
};

export const toArray = <T>(value: T | T[]): T[] =>
  Array.isArray(value) ? value : [value];

const toScores = (score: SymptomScore): number[] =>
  toArray(score).filter(
    (entry): entry is number => typeof entry === 'number' && !isNaN(entry)
  );

const resolveSeverityScore = (score: SymptomScore): number | null => {
  const scores = toScores(score);

  return scores.length > 0 ? Math.max(...scores) : null;
};

/**
 * On an exact tie between equal and opposite values the worse side wins. That
 * asymmetry is deliberate: given both extremes in one day, a provider should see
 * the one worth acting on.
 */
const resolveDirectionalScore = (
  score: SymptomScore,
  normalScore: number
): number | null => {
  const scores = toScores(score);

  if (scores.length === 0) {
    return null;
  }

  return scores.reduce((strongest, candidate) => {
    const candidateDistance = Math.abs(candidate - normalScore);
    const strongestDistance = Math.abs(strongest - normalScore);

    if (candidateDistance !== strongestDistance) {
      return candidateDistance > strongestDistance ? candidate : strongest;
    }

    return Math.max(candidate, strongest);
  });
};

const clampToScale = (
  score: number,
  minScore: number,
  maxScore: number
): number => Math.min(Math.max(score, minScore), maxScore);

export const getSymptomScore = (
  scores: JournalEntryScore | undefined,
  symptom: SymptomKey
): SymptomScore => scores?.[symptom];

/**
 * Symptoms whose raw option ladder in `symptom.type.ts` (the mobile app's
 * legacy vocabulary) has been manually checked to agree, position for
 * position, with `SYMPTOM_CATALOGUE`'s scored `values` ladder for the same
 * key. Matching on key name and option count alone isn't enough: `stool`,
 * `hours_of_sleep`, and `sleep_quality` share both with their catalogue
 * counterpart but order their options differently, so a positional lookup
 * would silently produce the wrong score rather than none. `acne`,
 * `digestion`, `urination_frequency`, `painful_sex`, `sex_drive`, and
 * `body_hair_growth` are excluded because their option counts don't even
 * match. Only add a symptom here after confirming both ladders line up.
 */
const POSITIONAL_FALLBACK_SYMPTOMS: ReadonlySet<string> = new Set([
  'hot_flashes',
  'breast_tenderness',
  'nausea',
  'cramps',
  'joint_pain',
  'back_pain',
  'headache',
  'migraine',
  'bloating',
  'painful_urination',
  'incontinence',
  'anxiety',
  'brain_fog',
  'memory_problems',
  'stress',
]);

const RAW_OPTIONS_BY_SYMPTOM = new Map<string, string[]>(
  (symptomDefinitions as RawSymptomDefinition[])
    .filter(
      (definition) =>
        POSITIONAL_FALLBACK_SYMPTOMS.has(definition.propertyName) &&
        Array.isArray(definition.options)
    )
    .map((definition) => [
      definition.propertyName,
      (definition.options ?? []).map((option) => option.value),
    ])
);

const toRawLabels = (rawValue: unknown): string[] =>
  toArray(rawValue).filter(
    (value): value is string => typeof value === 'string' && value.length > 0
  );

/**
 * Backend `symptom_scores[symptom]` sometimes stays null/missing even when the
 * raw journal field it should have derived from is populated (e.g. a day with
 * `hot_flashes: "Mild"` but no matching entry in `symptom_scores`). When that
 * happens, resolve a score client-side from the raw value's ordinal position
 * in the symptom's own catalogue scale, rather than leaving the day unscored
 * and undotted in the grid while its raw label still shows in the detail
 * panel.
 */
export const deriveScoreFromRawValue = (
  symptom: SymptomKey,
  rawValue: unknown
): SymptomScore => {
  const options = RAW_OPTIONS_BY_SYMPTOM.get(symptom);

  if (!options) {
    return undefined;
  }

  const definition = getSymptomDefinition(symptom);
  const scores = toRawLabels(rawValue)
    .map((label) => options.indexOf(label))
    .filter((index) => index >= 0 && index < definition.values.length)
    .map((index) => definition.values[index].score);

  if (scores.length === 0) {
    return undefined;
  }

  return scores.length === 1 ? scores[0] : scores;
};

export const getSymptomScoreWithFallback = (
  scores: JournalEntryScore | undefined,
  symptom: SymptomKey,
  rawValue: unknown
): SymptomScore => {
  const direct = getSymptomScore(scores, symptom);

  if (direct !== null && direct !== undefined) {
    return direct;
  }

  return deriveScoreFromRawValue(symptom, rawValue);
};

export function getSeverityIntensity(
  symptom: SeveritySymptomKey,
  score: SymptomScore
): SeverityIntensity {
  const definition = getSymptomDefinition(symptom);
  const stepCount = definition.maxScore - definition.minScore;
  const resolved = resolveSeverityScore(score);

  if (resolved === null) {
    return NOT_REPORTED;
  }

  const clamped = clampToScale(
    resolved,
    definition.minScore,
    definition.maxScore
  );
  const step = clamped - definition.minScore;

  if (step === 0) {
    return {
      state: 'none_reported',
      logic: 'severity',
      score: clamped,
      step: 0,
      stepCount,
      ratio: 0,
    };
  }

  return {
    state: 'reported',
    logic: 'severity',
    score: clamped,
    step,
    stepCount,
    ratio: stepCount === 0 ? 1 : step / stepCount,
  };
}

export function getDirectionalIntensity(
  symptom: DirectionalSymptomKey,
  score: SymptomScore
): DirectionalIntensity {
  const definition = getSymptomDefinition(symptom);
  const normalScore = (definition.minScore + definition.maxScore) / 2;
  const scaleLength = definition.maxScore - definition.minScore;
  const resolved = resolveDirectionalScore(score, normalScore);

  if (resolved === null) {
    return NOT_REPORTED;
  }

  const clamped = clampToScale(
    resolved,
    definition.minScore,
    definition.maxScore
  );
  return {
    state: 'reported',
    logic: 'directional',
    score: clamped,
    ratio:
      scaleLength === 0 ? 1 : (clamped - definition.minScore) / scaleLength,
    direction:
      clamped === normalScore
        ? 'normal'
        : clamped > normalScore
        ? 'worse_than_normal'
        : 'better_than_normal',
  };
}

export function getIntensity(
  symptom: DirectionalSymptomKey,
  score: SymptomScore
): DirectionalIntensity;
export function getIntensity(
  symptom: SeveritySymptomKey,
  score: SymptomScore
): SeverityIntensity;
export function getIntensity(
  symptom: SymptomKey,
  score: SymptomScore
): SymptomIntensity;
export function getIntensity(
  symptom: SymptomKey,
  score: SymptomScore
): SymptomIntensity {
  return isDirectionalSymptom(symptom)
    ? getDirectionalIntensity(symptom, score)
    : getSeverityIntensity(symptom, score);
}

export const isReported = (intensity: SymptomIntensity): boolean =>
  intensity.state !== 'not_reported';
